<script lang="ts">
  import { showAlert } from '../../lib/ui-alert';
  import { onMount } from 'svelte';
  import SettingsLayout from '../../components/SettingsLayout.svelte';
  import Icon from '../../components/Icon.svelte';
  import {
    fetchProductTree,
    fetchUnrecognizedTariffs,
    createProductGroup,
    createTariff,
    updateTariff,
    createLaunch,
    type ProductTree,
    type TariffNode,
    type UnrecognizedTariff,
  } from '../../lib/admin-api';
  import { fmtInt, fmtUah } from '../../lib/format';

  let tree = $state<ProductTree>({ products: [], ungroupedTariffs: [] });
  let unrec = $state<UnrecognizedTariff[]>([]);
  let loading = $state(true);
  let err = $state<string | null>(null);
  let expanded = $state<Set<string>>(new Set());

  const KINDS = ['subscription', 'minicourse', 'intensive', 'trial', 'addon'];

  async function load() {
    loading = true;
    err = null;
    try {
      const [t, u] = await Promise.all([fetchProductTree(), fetchUnrecognizedTariffs()]);
      tree = t;
      unrec = u;
    } catch (e) {
      err = e instanceof Error ? e.message : String(e);
    } finally {
      loading = false;
    }
  }
  onMount(load);
  function toggle(id: string) {
    const next = new Set(expanded);
    next.has(id) ? next.delete(id) : next.add(id);
    expanded = next;
  }

  /* ── create Product ───────────────────────────────────────────────────── */
  let showProduct = $state(false);
  let pName = $state('');
  let pKind = $state('subscription');
  async function onCreateProduct(e: SubmitEvent) {
    e.preventDefault();
    if (!pName) return;
    try {
      await createProductGroup({ name: pName, kind: pKind });
      showProduct = false;
      pName = '';
      await load();
    } catch (e2) {
      showAlert((e2 as Error).message);
    }
  }

  /* ── create Tariff ────────────────────────────────────────────────────── */
  let showTariff = $state(false);
  let tProductId = $state<string | null>(null);
  let tSku = $state('');
  let tName = $state('');
  let tKind = $state('subscription');
  let tPrice = $state('');
  let tMatcher = $state('');
  function openTariff(productId: string | null, prefillMatcher = '') {
    tProductId = productId;
    tMatcher = prefillMatcher;
    tSku = '';
    tName = '';
    tPrice = '';
    showTariff = true;
  }
  async function onCreateTariff(e: SubmitEvent) {
    e.preventDefault();
    if (!tSku || !tName || !tPrice || !tMatcher) return;
    try {
      const r = await createTariff({
        productId: tProductId,
        sku: tSku,
        name: tName,
        kind: tKind,
        priceCents: Math.round(Number(tPrice) * 100),
        matchers: [{ kind: 'tarif_eq', value: tMatcher }],
      });
      showTariff = false;
      showAlert(`Тариф створено. Back-fixed ${r.backfixedPayments} платежів.`);
      await load();
    } catch (e2) {
      showAlert((e2 as Error).message);
    }
  }
  async function regroup(t: TariffNode, productId: string | null) {
    try {
      await updateTariff(t.id, { productId });
      await load();
    } catch (e2) {
      showAlert((e2 as Error).message);
    }
  }

  /* ── create Launch ────────────────────────────────────────────────────── */
  let showLaunch = $state(false);
  let lProductId = $state<string | null>(null);
  let lName = $state('');
  let lFrom = $state('');
  let lTo = $state('');
  let lRegex = $state('');
  function openLaunch(productId: string | null) {
    lProductId = productId;
    lName = '';
    lFrom = '';
    lTo = '';
    lRegex = '';
    showLaunch = true;
  }
  async function onCreateLaunch(e: SubmitEvent) {
    e.preventDefault();
    if (!lName) return;
    const matchers: unknown[] = [];
    if (lFrom || lTo) matchers.push({ kind: 'date_between', from: lFrom || undefined, to: lTo || undefined });
    if (lRegex) matchers.push({ kind: 'rawtariff_regex', pattern: lRegex });
    try {
      const r = await createLaunch({
        productId: lProductId,
        name: lName,
        startsAt: lFrom ? new Date(lFrom).toISOString() : null,
        endsAt: lTo ? new Date(lTo).toISOString() : null,
        matchers,
      });
      showLaunch = false;
      showAlert(`Запуск створено. Back-fixed ${r.backfixedPayments} платежів.`);
      await load();
    } catch (e2) {
      showAlert((e2 as Error).message);
    }
  }
</script>

{#snippet tariffRow(t: TariffNode)}
  <div class="flex items-center gap-2 rounded-md border border-[var(--color-border-3)] bg-white px-2.5 py-1.5">
    <Icon name="receipt" size={13} class="text-[var(--color-text-muted-2)]" />
    <span class="font-mono text-[11px]">{t.sku}</span>
    <span class="text-[12px] truncate">{t.name}</span>
    <span class="rounded-full bg-purple-100 px-1.5 py-0.5 text-[9px] font-bold text-purple-700">{t.kind}</span>
    <span class="font-bold tabular-nums text-[11px]">{fmtUah(t.priceCents)}</span>
    <span class="ml-auto text-[10px] text-[var(--color-text-muted)] tabular-nums">{fmtInt(t.payments30d)} оп · {fmtUah(t.revenue30dCents)} / 30д</span>
    {#if !t.active}<span class="text-[9px] text-rose-600 font-bold">inactive</span>{/if}
  </div>
{/snippet}

<SettingsLayout active="products">
  <div class="mx-auto max-w-[1200px]">
    <header class="mb-5 flex items-end justify-between gap-3 flex-wrap">
      <div>
        <h1 class="text-xl font-semibold leading-tight">Продукти · Тарифи · Запуски</h1>
        <p class="mt-1 text-[12px] text-[var(--color-text-muted)]">
          Product (що) → Tariff (як, matchers rawTariff) → Launch (коли, rule-based attribution)
        </p>
      </div>
      <div class="flex gap-2">
        <button onclick={() => openTariff(null)} class="rounded-md border border-[var(--color-border-3)] bg-white px-3 py-1.5 text-xs font-semibold">+ Тариф</button>
        <button onclick={() => (showProduct = !showProduct)} class="rounded-md bg-[var(--color-pu)] px-3 py-1.5 text-xs font-semibold text-white">+ Продукт</button>
      </div>
    </header>

    {#if loading}
      <div class="py-20 text-center text-sm text-[var(--color-text-muted)]">Завантаження…</div>
    {:else if err}
      <div class="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">{err}</div>
    {:else}
      {#if unrec.length > 0}
        <div class="rounded-xl border border-amber-300 bg-gradient-to-b from-amber-100 to-amber-50 p-4 mb-4">
          <div class="flex items-center gap-2.5 mb-3">
            <div class="inline-flex text-amber-600"><Icon name="warning" size={18} /></div>
            <div>
              <div class="text-[13px] font-bold text-amber-900">{unrec.length} нерозпізнаних тарифів</div>
              <div class="text-[11px] text-amber-700">У платежах за 30 днів без тарифу · клік = створити тариф з цим matcher'ом</div>
            </div>
          </div>
          <div class="flex flex-wrap gap-1.5">
            {#each unrec as u (u.rawTariff)}
              <button onclick={() => openTariff(null, u.rawTariff)} class="inline-flex items-center gap-1.5 rounded-md border border-amber-400 bg-white px-2.5 py-1 text-[11px] text-amber-900 font-medium hover:bg-amber-100">
                {u.rawTariff} <span class="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold">{u.count}</span>
              </button>
            {/each}
          </div>
        </div>
      {/if}

      {#if showProduct}
        <form onsubmit={onCreateProduct} class="rounded-xl border border-[var(--color-border-3)] bg-white p-4 mb-4 grid grid-cols-[1fr_auto_auto_auto] gap-2 items-end">
          <label class="block"><div class="text-[10px] font-semibold uppercase text-[var(--color-text-muted-2)] mb-1">Назва продукту</div>
            <input bind:value={pName} required class="w-full rounded-md border border-[var(--color-border-3)] px-2.5 py-1.5 text-xs" placeholder="FocusOnYou" /></label>
          <select bind:value={pKind} class="rounded-md border border-[var(--color-border-3)] px-2.5 py-1.5 text-xs">{#each KINDS as k}<option value={k}>{k}</option>{/each}</select>
          <button type="submit" class="rounded-md bg-[var(--color-pu)] px-3 py-1.5 text-xs font-semibold text-white">Створити</button>
          <button type="button" onclick={() => (showProduct = false)} class="rounded-md border border-[var(--color-border-3)] px-3 py-1.5 text-xs">×</button>
        </form>
      {/if}

      <div class="space-y-2.5">
        {#each tree.products as p (p.id)}
          <div class="rounded-xl border border-[var(--color-border-3)] bg-white overflow-hidden">
            <button onclick={() => toggle(p.id)} class="w-full flex items-center gap-2.5 px-4 py-3 text-left hover:bg-[var(--color-bg-3)]">
              <Icon name={expanded.has(p.id) ? 'chevronDown' : 'chevronRight'} size={15} />
              <Icon name="package" size={16} class="text-[var(--color-pu)]" />
              <span class="font-semibold text-sm">{p.name}</span>
              <span class="rounded-full bg-purple-100 px-2 py-0.5 text-[10px] font-bold text-purple-700">{p.kind}</span>
              <span class="text-[10px] text-[var(--color-text-muted-2)] font-mono">{p.slug}</span>
              <span class="ml-auto text-[11px] text-[var(--color-text-muted)] tabular-nums">{p.tariffs.length} тарифів · {fmtUah(p.revenue30dCents)} / 30д</span>
            </button>
            {#if expanded.has(p.id)}
              <div class="border-t border-[var(--color-border-3)] px-4 py-3 space-y-2 bg-[var(--color-bg-3)]/40">
                <div class="flex items-center justify-between">
                  <span class="text-[10px] font-bold uppercase tracking-wider text-[var(--color-text-muted-2)]">Тарифи</span>
                  <button onclick={() => openTariff(p.id)} class="text-[11px] font-semibold text-[var(--color-pu-3)] hover:underline">+ тариф</button>
                </div>
                {#each p.tariffs as t (t.id)}{@render tariffRow(t)}{/each}
                {#if p.tariffs.length === 0}<div class="text-[11px] italic text-[var(--color-text-muted-2)]">Немає тарифів</div>{/if}
                <div class="flex items-center justify-between pt-1.5">
                  <span class="text-[10px] font-bold uppercase tracking-wider text-[var(--color-text-muted-2)]">Запуски</span>
                  <button onclick={() => openLaunch(p.id)} class="text-[11px] font-semibold text-[var(--color-pu-3)] hover:underline">+ запуск</button>
                </div>
                {#each p.launches as l (l.id)}
                  <div class="flex items-center gap-2 rounded-md border border-[var(--color-border-3)] bg-white px-2.5 py-1.5 text-[11px]">
                    <Icon name="rocket" size={12} class="text-[var(--color-text-muted-2)]" />
                    <span class="font-semibold">{l.name}</span>
                    <span class="text-[10px] text-[var(--color-text-muted)]">{l.startsAt?.slice(0, 10) ?? '—'} → {l.endsAt?.slice(0, 10) ?? '—'}</span>
                    <span class="ml-auto text-[10px] text-[var(--color-text-muted)] tabular-nums">{fmtInt(l.payments30d)} оп · {fmtUah(l.revenue30dCents)} / 30д</span>
                  </div>
                {/each}
                {#if p.launches.length === 0}<div class="text-[11px] italic text-[var(--color-text-muted-2)]">Немає запусків</div>{/if}
              </div>
            {/if}
          </div>
        {/each}
      </div>

      {#if tree.ungroupedTariffs.length > 0}
        <div class="mt-4 rounded-xl border border-dashed border-[var(--color-border-3)] bg-[var(--color-bg-3)] p-4">
          <div class="text-[11px] font-bold uppercase tracking-wider text-[var(--color-text-muted-2)] mb-2">
            Тарифи без продукту ({tree.ungroupedTariffs.length}) — призначте продукт-батько
          </div>
          <div class="space-y-1.5">
            {#each tree.ungroupedTariffs as t (t.id)}
              <div class="flex items-center gap-2">
                <div class="flex-1">{@render tariffRow(t)}</div>
                <select onchange={(e) => regroup(t, (e.target as HTMLSelectElement).value || null)} class="rounded-md border border-[var(--color-border-3)] px-2 py-1.5 text-[11px]">
                  <option value="">— продукт —</option>
                  {#each tree.products as p}<option value={p.id}>{p.name}</option>{/each}
                </select>
              </div>
            {/each}
          </div>
        </div>
      {/if}

      {#if tree.products.length === 0 && tree.ungroupedTariffs.length === 0}
        <div class="py-12 text-center text-xs text-[var(--color-text-muted)]">Поки немає продуктів. Створіть перший.</div>
      {/if}
    {/if}
  </div>

  <!-- Tariff create modal -->
  {#if showTariff}
    <div class="fixed inset-0 z-[60] grid place-items-center bg-black/40 p-6" role="presentation" onclick={() => (showTariff = false)}>
      <form onsubmit={onCreateTariff} class="w-full max-w-lg rounded-2xl bg-white p-5 shadow-2xl" role="dialog" tabindex="-1" onclick={(e) => e.stopPropagation()}>
        <div class="text-sm font-bold mb-3">Новий тариф {tProductId ? '' : '(без продукту)'}</div>
        <div class="grid grid-cols-2 gap-3">
          <label class="block"><div class="text-[10px] font-semibold uppercase text-[var(--color-text-muted-2)] mb-1">SKU</div><input bind:value={tSku} required class="w-full rounded-md border border-[var(--color-border-3)] px-2.5 py-1.5 text-xs font-mono" placeholder="focusonyou-1m" /></label>
          <label class="block"><div class="text-[10px] font-semibold uppercase text-[var(--color-text-muted-2)] mb-1">Назва</div><input bind:value={tName} required class="w-full rounded-md border border-[var(--color-border-3)] px-2.5 py-1.5 text-xs" placeholder="FocusOnYou 1 місяць" /></label>
          <label class="block"><div class="text-[10px] font-semibold uppercase text-[var(--color-text-muted-2)] mb-1">Kind</div><select bind:value={tKind} class="w-full rounded-md border border-[var(--color-border-3)] px-2.5 py-1.5 text-xs">{#each KINDS as k}<option value={k}>{k}</option>{/each}</select></label>
          <label class="block"><div class="text-[10px] font-semibold uppercase text-[var(--color-text-muted-2)] mb-1">Ціна (UAH)</div><input bind:value={tPrice} type="number" min="0" step="0.01" required class="w-full rounded-md border border-[var(--color-border-3)] px-2.5 py-1.5 text-xs" placeholder="240" /></label>
          <label class="block col-span-2"><div class="text-[10px] font-semibold uppercase text-[var(--color-text-muted-2)] mb-1">tarif_eq matcher (rawTariff)</div><input bind:value={tMatcher} required class="w-full rounded-md border border-[var(--color-border-3)] px-2.5 py-1.5 text-xs font-mono" placeholder="focusonyou-місяць" /></label>
        </div>
        <div class="mt-4 flex gap-2 justify-end">
          <button type="button" onclick={() => (showTariff = false)} class="rounded-md border border-[var(--color-border-3)] px-3 py-1.5 text-xs font-semibold">Скасувати</button>
          <button type="submit" class="rounded-md bg-[var(--color-pu)] px-3 py-1.5 text-xs font-semibold text-white">Створити</button>
        </div>
      </form>
    </div>
  {/if}

  <!-- Launch create modal -->
  {#if showLaunch}
    <div class="fixed inset-0 z-[60] grid place-items-center bg-black/40 p-6" role="presentation" onclick={() => (showLaunch = false)}>
      <form onsubmit={onCreateLaunch} class="w-full max-w-lg rounded-2xl bg-white p-5 shadow-2xl" role="dialog" tabindex="-1" onclick={(e) => e.stopPropagation()}>
        <div class="text-sm font-bold mb-3">Новий запуск</div>
        <div class="grid grid-cols-2 gap-3">
          <label class="block col-span-2"><div class="text-[10px] font-semibold uppercase text-[var(--color-text-muted-2)] mb-1">Назва</div><input bind:value={lName} required class="w-full rounded-md border border-[var(--color-border-3)] px-2.5 py-1.5 text-xs" placeholder="June cohort 2026" /></label>
          <label class="block"><div class="text-[10px] font-semibold uppercase text-[var(--color-text-muted-2)] mb-1">Старт</div><input bind:value={lFrom} type="date" class="w-full rounded-md border border-[var(--color-border-3)] px-2.5 py-1.5 text-xs" /></label>
          <label class="block"><div class="text-[10px] font-semibold uppercase text-[var(--color-text-muted-2)] mb-1">Кінець</div><input bind:value={lTo} type="date" class="w-full rounded-md border border-[var(--color-border-3)] px-2.5 py-1.5 text-xs" /></label>
          <label class="block col-span-2"><div class="text-[10px] font-semibold uppercase text-[var(--color-text-muted-2)] mb-1">rawtariff_regex (опц.)</div><input bind:value={lRegex} class="w-full rounded-md border border-[var(--color-border-3)] px-2.5 py-1.5 text-xs font-mono" placeholder="june|червень" /></label>
        </div>
        <p class="mt-2 text-[10px] text-[var(--color-text-muted)]">Атрибуція: правила (date/regex) АБО time-window fallback за датою оплати.</p>
        <div class="mt-4 flex gap-2 justify-end">
          <button type="button" onclick={() => (showLaunch = false)} class="rounded-md border border-[var(--color-border-3)] px-3 py-1.5 text-xs font-semibold">Скасувати</button>
          <button type="submit" class="rounded-md bg-[var(--color-pu)] px-3 py-1.5 text-xs font-semibold text-white">Створити</button>
        </div>
      </form>
    </div>
  {/if}
</SettingsLayout>
